import React, { FC, useEffect, useState } from 'react';
import Modal from '~ComponentsImproved/Modal';
import { uniqueId } from 'lodash';
import { useButton, useI18n, useLogger } from '~Hooks';
import styled from '@emotion/styled';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { BlueButton } from '~Styles';
import Icon from '~ComponentsImproved/Icon';
import CloseIcon from '~Assets/icons/close.svg';
import { Button } from '~Styles';
import GeneralStarGuide from './GeneralStarGuide';
import GeneralEpsonGuide from './GeneralEpsonGuide';

const TroubleShootWrapper = styled.div`
  padding: 40px;
  background: var(--colorBlueGray1200);
  border-radius: 8px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.2);
  width: calc(100% - 64px);
  max-width: 980px;
  height: calc(100% - 64px);
  overflow-y: auto;
  text-align: center;
  position: relative;
  box-sizing: border-box;
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
`;

const CloseButton = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  height: 40px;
  width: 40px;
  background: var(--colorBlueGray100);
`;

export const PickPrinterButtons = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 32px;
  width: 100%;
  max-width: 600px;
  align-items: center;
  justify-content: center;

  button {
    width: 100%;
    max-width: 300px;
  }
`;

const TroubleShootButton = styled(Button)`
  margin: 8px;
  width: 20%;
  padding: 16px;
  background: var(--colorBlueGray100);
  font-size: 14px;
  color: var(--colorUIBlue);
  gap: 8px;
`;

export const TroubleShootingTextWrapper = styled.div`
  max-width: 600px;
`;

interface Props {
  startPrinterScan: () => void;
}

const NoPrinterTroubleshooting: FC<Props> = ({ startPrinterScan }) => {
  const logger = useLogger('printer-troubleshooting');
  const [troubleshoot, setTroubleshoot] = useState(false);
  const { i18n } = useI18n();
  const sessionId = uniqueId(); // For datadog logging purposes
  const [printSelect, setPrintSelect] = useState(true);
  const [isStar, setIsStar] = useState(false);

  useEffect(() => {
    if (troubleshoot === false) {
      setPrintSelect(true);
      setIsStar(false);
    }
  }, [troubleshoot]);

  //Closes the guide and starts scanning for printers
  const retryScan = () => {
    setTroubleshoot(false);
    startPrinterScan();
  };

  //Multiple button states to handle the opening of the guide and also the selection of the Epson or Star guides
  const TroubleshootButton = useButton({
    onClick: () => {
      logger.info('Unable to find printer pressed', {
        sessionId,
      });
      setTroubleshoot(true);
    },
  });

  const EpsonButton = useButton({
    onClick: () => {
      logger.info('Epson guide selected', {
        sessionId,
      });
      setPrintSelect(false);
    },
  });

  const StarButton = useButton({
    onClick: () => {
      logger.info('Star guide selected', {
        sessionId,
      });
      setPrintSelect(false);
      setIsStar(true);
    },
  });

  return (
    <>
      <TroubleShootButton {...TroubleshootButton}>
        {i18n('Settings.Printers.TroubleInfo.Button')}
      </TroubleShootButton>

      <Modal isShowing={troubleshoot} backdropClose hide={() => setTroubleshoot(false)} noPadding>
        <TroubleShootWrapper>
          <CloseButton onTouchStart={() => setTroubleshoot(false)}>
            <Icon src={CloseIcon} color="#000" />
          </CloseButton>
          <InnerWrapper>
            {printSelect ? (
              <>
                <TroubleShootingTextWrapper>
                  <Heading level={1} color="#fff" bottom={16}>
                    {i18n('Printer.Troubleshooting.Model')}
                  </Heading>
                  <Paragraph level={2} color="var(--colorBlueGray200)">
                    {i18n('Printer.Troubleshooting.Model.Desc')}
                  </Paragraph>
                </TroubleShootingTextWrapper>
                <PickPrinterButtons>
                  <BlueButton {...EpsonButton}>Epson</BlueButton>
                  <BlueButton {...StarButton}>Star</BlueButton>
                </PickPrinterButtons>
              </>
            ) : (
              <>
                {isStar ? (
                  <GeneralStarGuide retryScan={retryScan} sessionId={sessionId} />
                ) : (
                  <GeneralEpsonGuide retryScan={retryScan} sessionId={sessionId} />
                )}
              </>
            )}
          </InnerWrapper>
        </TroubleShootWrapper>
      </Modal>
    </>
  );
};

export default NoPrinterTroubleshooting;
